import type { ConvoyState } from '@konvoi/shared';

/**
 * Die Zeichen der Kolonnenzustände.
 *
 * Dieselbe Begründung wie bei den Meldungen (lib/meldezeichen.tsx): Vorher
 * standen hier Schriftzeichen — ● ▼ ✕ ⤳ ■ ⚠ ❚❚ — mit jeweils eigenen Maßen,
 * eigener Strichstärke und, je nach Gerät, eigener Farbe. Untereinander in der
 * Teilnehmerliste ergab das eine unruhige Spalte. Gezeichnet haben alle
 * dieselbe Fläche und dieselbe Strichstärke.
 *
 * ## Warum sie mit der Schrift wachsen
 *
 * Anders als die Meldungszeichen erscheinen sie an zwei Stellen in
 * verschiedenen Größen: klein im Merkmal der Teilnehmerliste, größer im Kopf
 * des Warnbalkens. Deshalb `1em` statt fester Pixel — sie folgen der Schrift,
 * neben der sie stehen.
 *
 * ## Und warum die Farbe nicht allein steht
 *
 * Ein Achtel der Männer sieht Rot und Grün nicht auseinander, und bei Sonne auf
 * dem Display geht Farbe als Erstes verloren. Jeder Zustand hat deshalb sein
 * eigenes Bild und sein eigenes Wort; die Farbe kommt hinzu, trägt aber nichts
 * allein.
 */

const STRICH = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

function Rahmen({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg
      className="zustandszeichen"
      viewBox="0 0 20 20"
      width="1em"
      height="1em"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/**
 * Unsichtbar geschaltet.
 *
 * Kein Kolonnenzustand, sondern eine Einstellung — steht aber an derselben
 * Stelle und muss deshalb dieselbe Gestalt haben. Der durchgestrichene Kreis
 * ist dasselbe Bild wie ⊘, nur mit einer Strichstärke, die auch klein trägt:
 * Vorher stand hier ◌, ein gepunkteter Kreis, der bei Sonne als Erstes
 * verschwand.
 */
export function ZeichenUnsichtbar(): React.JSX.Element {
  return (
    <Rahmen>
      <circle cx="10" cy="10" r="7.6" {...STRICH} />
      <path d="M4.6 15.4 15.4 4.6" {...STRICH} />
    </Rahmen>
  );
}

/**
 * Eine Ansage der Führung wurde gelesen.
 *
 * Das Häkchen — das eine Bild, das dafür überall steht. Es erscheint nur,
 * solange eine eigene Ansage offen ist, und tritt deshalb bewusst ruhig auf:
 * Die Führung sucht in dieser Liste nicht das Erledigte, sondern die Lücken.
 */
export function ZeichenGelesen(): React.JSX.Element {
  return (
    <Rahmen>
      <path d="M4 10.5 8.3 15 16 5.5" {...STRICH} />
    </Rahmen>
  );
}

/**
 * Ungelesen.
 *
 * Die Uhr, nicht das Ausrufezeichen: Es ist kein Fehler, wenn jemand eine
 * Meldung noch nicht weggetippt hat — er fährt gerade. Gemeint ist „steht noch
 * aus", und genau das zeigt eine wartende Uhr.
 */
export function ZeichenOffen(): React.JSX.Element {
  return (
    <Rahmen>
      <circle cx="10" cy="10" r="7.2" {...STRICH} />
      <path d="M10 5.6V10l3 2" {...STRICH} />
    </Rahmen>
  );
}

export function ZustandsZeichen({ state }: { state: ConvoyState }): React.JSX.Element {
  switch (state) {
    case 'in_contact':
      // Voller Punkt: dabei, geschlossen, nichts zu tun.
      return (
        <Rahmen>
          <circle cx="10" cy="10" r="6.4" fill="currentColor" />
        </Rahmen>
      );

    case 'lagging':
      // Ein Winkel nach unten — noch keine Warnung, aber es geht abwärts.
      return (
        <Rahmen>
          <path d="M4.4 7.4 10 13 15.6 7.4" {...STRICH} />
        </Rahmen>
      );

    case 'lost_contact':
      // Das Kreuz: die Verbindung zur Kolonne ist gerissen.
      return (
        <Rahmen>
          <path d="M5 5 15 15" {...STRICH} />
          <path d="M15 5 5 15" {...STRICH} />
        </Rahmen>
      );

    case 'off_route':
      // Ein Weg, der abzweigt: geradeaus die Strecke, rechts hinaus das
      // Fahrzeug. Deshalb kein Kreuz — falsch abgebogen ist etwas anderes als
      // abgehängt, und die Führung muss beides auseinanderhalten.
      return (
        <Rahmen>
          <path d="M9 17.5V10l6.5-6.5" {...STRICH} />
          <path d="M10.5 3.5h5.5V9" {...STRICH} />
        </Rahmen>
      );

    case 'standstill':
      // Das gefüllte Quadrat — dasselbe Zeichen wie „Wir halten an".
      return (
        <Rahmen>
          <rect x="4.6" y="4.6" width="10.8" height="10.8" rx="1.6" fill="currentColor" />
        </Rahmen>
      );

    case 'no_signal':
      /*
       * Zwei aufsteigende Balken mit Strich: kein Empfang.
       *
       * Zuerst waren es drei Balken — bei Abzeichengröße wurde daraus ein
       * Gekritzel. Bei siebzehn Pixeln trägt kein Zeichen mehr als drei
       * Striche. Das Warndreieck von vorher sagte ohnehin nur „irgendetwas
       * stimmt nicht"; hier stimmt genau eines nicht, und zwar der Funk.
       */
      return (
        <Rahmen>
          <path d="M5.5 15.5V11" {...STRICH} />
          <path d="M11 15.5V6" {...STRICH} />
          <path d="M3.5 16.5 16.5 3.5" {...STRICH} />
        </Rahmen>
      );

    case 'paused':
      // Zwei Balken: das Gerät sendet nicht, die Fahrt ruht.
      return (
        <Rahmen>
          <rect x="5.2" y="4.2" width="3.2" height="11.6" rx="1.1" fill="currentColor" />
          <rect x="11.6" y="4.2" width="3.2" height="11.6" rx="1.1" fill="currentColor" />
        </Rahmen>
      );
  }
}
